import React from 'react';
import { useModal } from 'hooks/useModal';
import { useHistory } from 'react-router';
import { useNotifications } from 'hooks/useNotifications';
import ConfirmationModal, { EConfirmationModalType } from 'ui/ConfirmationModal';
import { makeItineraryManagerApi } from 'services/ItineraryManagerApi';
import { ItineraryTypes } from './types';
import { useItinerarySaveAndPublishQueries } from './useItinerarySaveAndPublishQueries';

// Helper to check if note is empty (accounting for EditorQuill HTML)
const isNoteEmpty = (noteText: string): boolean => {
  const stripped = noteText.replace(/<[^>]*>/g, '').trim();
  return stripped.length === 0;
};

// Filter empty notes from items before saving
const filterEmptyNotes = (
  items: ItineraryTypes['ItineraryItem'][]
): ItineraryTypes['ItineraryItem'][] => {
  return items.map(item => ({
    ...item,
    notes: item.notes.filter(note => !isNoteEmpty(note.text)),
  }));
};

export const useItinerarySaveAndPublishFlow = (opts: {
  bookingUuid: string;
  version: string;
  isEditedWithoutSaving?: boolean;
  onSaveSuccess?: () => void;
  onSaveError?: () => void;
  onPublishSuccess?: () => void;
  onPublishError?: () => void;
}) => {
  const confirmPublishModal = useModal();
  const history = useHistory();
  const notifications = useNotifications();

  const itinerarySaveAndPublishQueries = useItinerarySaveAndPublishQueries(
    makeItineraryManagerApi(),
    opts.bookingUuid,
    opts.version
  );

  const onSave = async (saveOpts: {
    savedItineraryInstance;
    datedItems;
    undatedItems;
    subtitle;
    logoImage;
    headerImage;
    isTripOverviewVisible;
  }) => {
    // Filter out empty notes before saving
    const cleanDatedItems = filterEmptyNotes(saveOpts.datedItems);
    const cleanUndatedItems = filterEmptyNotes(saveOpts.undatedItems);

    try {
      await itinerarySaveAndPublishQueries.saveItinerary.mutateAsync({
        ...saveOpts.savedItineraryInstance!,
        datedItems: cleanDatedItems as ItineraryTypes['DatabaseItineraryItem'][],
        undatedItems: cleanUndatedItems as ItineraryTypes['DatabaseItineraryItem'][],
        subtitle: saveOpts.subtitle,
        logoImage: saveOpts.logoImage!,
        headerImage: saveOpts.headerImage!,
        isTripOverviewVisible: saveOpts.isTripOverviewVisible,
      });
      notifications.showSuccessNotification(`Itinerary version ${opts.version} saved`);
      if (opts.onSaveSuccess) {
        opts.onSaveSuccess();
      }
      history.push(`/itinerary/${opts.bookingUuid}/${opts.version}`);
    } catch (e) {
      notifications.showErrorNotification('Saving error, please try later');
      if (opts.onSaveError) {
        opts.onSaveError();
      }
    }
  };

  const onPublish = async (
    publishOpts?:
      | {
          savedItineraryInstance;
          datedItems;
          undatedItems;
          subtitle;
          logoImage;
          headerImage;
          isTripOverviewVisible;
        }
      | undefined
  ) => {
    // first warn them with a modal
    const shouldPublish = await confirmPublishModal.requestConfirmation();
    if (!shouldPublish) {
      return;
    }

    // if they've made changes, save those changes first...
    if (opts.isEditedWithoutSaving && opts.isEditedWithoutSaving === true) {
      // Filter out empty notes before saving
      const cleanDatedItems = filterEmptyNotes(publishOpts?.datedItems || []);
      const cleanUndatedItems = filterEmptyNotes(publishOpts?.undatedItems || []);

      try {
        await itinerarySaveAndPublishQueries.saveItinerary.mutateAsync({
          ...publishOpts?.savedItineraryInstance!,
          datedItems: cleanDatedItems as ItineraryTypes['DatabaseItineraryItem'][],
          undatedItems: cleanUndatedItems as ItineraryTypes['DatabaseItineraryItem'][],
          subtitle: publishOpts?.subtitle,
          logoImage: publishOpts?.logoImage!,
          headerImage: publishOpts?.headerImage!,
          isTripOverviewVisible: publishOpts?.isTripOverviewVisible,
        });
        // the the pre-publish save was successful, publish the changes
        try {
          await itinerarySaveAndPublishQueries.publishItinerary.mutateAsync();
          notifications.showSuccessNotification('Itinerary version saved and published successfully.');
          // so that edited is false by the time we call history.push
          if (opts.onPublishSuccess) {
            opts.onPublishSuccess();
          }
          history.push(`/itinerary/${opts.bookingUuid}/${opts.version}`);
        } catch (e) {
          notifications.showErrorNotification('Failed to publish itinerary, please try again later');
          if (opts.onPublishError) {
            opts.onPublishError();
          }
        }
      } catch (e) {
        notifications.showErrorNotification('Error while saving during publish, please try again later');
      }
    } else {
      // ...otherwise just publish the changes
      try {
        await itinerarySaveAndPublishQueries.publishItinerary.mutateAsync();
        notifications.showSuccessNotification('Itinerary version published successfully');
        if (opts.onPublishSuccess) {
          opts.onPublishSuccess();
        }
        history.push(`/itinerary/${opts.bookingUuid}/${opts.version}`);
      } catch (e) {
        notifications.showErrorNotification('Failed to publish itinerary, please try again later');
        if (opts.onPublishError) {
          opts.onPublishError();
        }
      }
    }
  };

  const render = () => {
    return (
      <>
        {confirmPublishModal.isOpen && (
          <ConfirmationModal
            className="leave-without-saving z-30"
            type={EConfirmationModalType.WARNING}
            isOpen
            title={`You are about to publish this version. This action will create a new published version that can be shared.`}
            message={'Are you sure?'}
            confirmButtonLabel={'Yes'}
            cancelButtonLabel={'No'}
            onConfirm={() => {
              confirmPublishModal.handleConfirm(true);
            }}
            onCancel={() => {
              confirmPublishModal.handleClose();
            }}
          />
        )}
      </>
    );
  };

  const isSavePending = itinerarySaveAndPublishQueries.saveItinerary.isPending;
  const isPublishPending = itinerarySaveAndPublishQueries.publishItinerary.isPending;

  return {
    onSave,
    onPublish,
    isSavePending,
    isPublishPending,
    render,
  };
};
